import { parseSecureRef, templateTokenRegex } from "@productlane/lib"

/**
 * The editors show a qualified http ref as `http."<connector name>".<key>` and
 * store it as `http.<sectionId>.<key>`. The shown text IS the edited text, so
 * the caret always lands on real glyphs; this codec maps between the two forms.
 */
export interface RefDisplayMaps {
	toTitle: ReadonlyMap<string, string>
	toId: ReadonlyMap<string, string>
}

// A title with a quote or a brace could not survive the trip back out of a
// token body, so those connectors keep their raw id form.
const SAFE_TITLE = /^[^"{}\n\r]+$/

const EMPTY_MAPS: RefDisplayMaps = { toTitle: new Map(), toId: new Map() }

export function refDisplayMaps(
	titles: Readonly<Record<string, string>>,
): RefDisplayMaps {
	const counts = new Map<string, number>()
	for (const title of Object.values(titles)) {
		counts.set(title, (counts.get(title) ?? 0) + 1)
	}

	const toTitle = new Map<string, string>()
	const toId = new Map<string, string>()
	for (const [id, title] of Object.entries(titles)) {
		// A duplicated title cannot name one connector, so it stays an id.
		if (!SAFE_TITLE.test(title) || counts.get(title) !== 1) continue
		toTitle.set(id, title)
		toId.set(title, id)
	}
	return toTitle.size > 0 ? { toTitle, toId } : EMPTY_MAPS
}

// Greedy name up to the last `".` ahead of a valid key, so a title that itself
// contains `".` still reads whole.
const DISPLAY_REF = /^http\."(.+)"\.([A-Za-z][A-Za-z0-9_-]{0,63})$/

function mapTokenBodies(text: string, map: (body: string) => string): string {
	return text.replace(templateTokenRegex(), (token, body: string) => {
		const next = map(body)
		if (next === body) return token
		const at = token.indexOf(body)
		return token.slice(0, at) + next + token.slice(at + body.length)
	})
}

/** One ref, storage form to display form; unchanged when no title answers. */
export function decodeRef(ref: string, maps: RefDisplayMaps): string {
	const parsed = parseSecureRef(ref)
	if (!parsed || parsed.namespace !== "http" || !parsed.sectionId) return ref
	const title = maps.toTitle.get(parsed.sectionId)
	return title ? `http."${title}".${parsed.key}` : ref
}

/** One ref, display form back to storage; an unknown name stays as typed. */
export function encodeRef(ref: string, maps: RefDisplayMaps): string {
	const match = DISPLAY_REF.exec(ref)
	if (!match) return ref
	const id = maps.toId.get(match[1]!)
	return id ? `http.${id}.${match[2]!}` : ref
}

/** Template or JSON text, storage form to display form, token bodies only. */
export function decodeRefText(text: string, maps: RefDisplayMaps): string {
	if (maps.toTitle.size === 0 || !text.includes("{{")) return text
	return mapTokenBodies(text, (body) => decodeRef(body, maps))
}

/** Template or JSON text, display form back to storage, token bodies only. */
export function encodeRefText(text: string, maps: RefDisplayMaps): string {
	if (maps.toId.size === 0 || !text.includes("{{")) return text
	return mapTokenBodies(text, (body) => encodeRef(body, maps))
}
